import { FlexColumn } from "@opensea/ui-kit"
import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { useIsAbstractWalletAccount } from "../../../hooks/useIsAbstractWalletAccount"
import { EMBEDDED_WALLET_CONNECTOR_ID } from "../../../lib/wagmi"
import { useAccount, useConnect } from "../../../polychain/hooks"
import { WalletRejectedError } from "../../../polychain/lib/errors"
import { FlowActions, FlowState } from "../../../stores/flowStore"
import type { CheckoutFlowState } from "../../flow-state"
import { useErrors } from "../../hooks/useErrors"
import { CheckoutPaymentStep } from "../CheckoutPaymentStep/CheckoutPaymentStep"
import { PaymentMethodStep } from "../PaymentMethodStep"
import type { PaymentMethod, PaymentStep } from "../types"
import { useCardCheckout } from "../use-card-checkout"

type Props = {
  cardSupported: boolean
  currencySelector: ReactNode
  timeline: ReactNode
  hidePaymentMethod?: boolean
}

export const CheckoutPaymentSection = ({
  timeline,
  cardSupported,
  currencySelector,
  hidePaymentMethod,
}: Props) => {
  const t = useTranslations("wallet.CheckoutPaymentSection")
  const { flow } = useSnapshot(FlowState)
  const checkoutFlow =
    flow?.type === "checkout" ? (flow as CheckoutFlowState) : undefined

  const cardCheckout = useCardCheckout()

  const { connectorId } = useAccount()

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(
    checkoutFlow?.paymentMethod ??
      (connectorId ? `wallet-${connectorId}` : undefined),
  )
  const [paymentStep, setPaymentStep] = useState<PaymentStep>(
    hidePaymentMethod || paymentMethod ? "payment" : "payment-method",
  )

  useEffect(() => {
    FlowActions.setFlowField<CheckoutFlowState>("paymentMethod", paymentMethod)
  }, [paymentMethod])

  const { setWalletError, clearErrors } = useErrors()
  const { isConnecting, connect, getConnectorStrict } = useConnect()
  const onProceedToPayment = useCallback(async () => {
    if (paymentMethod === "card") {
      cardCheckout()
      return
    }

    const connectorId = paymentMethod?.slice("wallet-".length)
    if (!connectorId) {
      return
    }
    if (connectorId === EMBEDDED_WALLET_CONNECTOR_ID) {
      const embeddedWalletConnector = getConnectorStrict(
        EMBEDDED_WALLET_CONNECTOR_ID,
      )
      if (embeddedWalletConnector.isConnected()) {
        await connect(embeddedWalletConnector)
      }
      setPaymentStep("payment")
      return
    }
    const connector = getConnectorStrict(connectorId)
    if (isConnecting) {
      return
    }
    clearErrors()
    try {
      await connect(connector)
      setPaymentStep("payment")
    } catch (error) {
      if (error instanceof WalletRejectedError) {
        setWalletError("Rejected")
      } else {
        setWalletError("Error")
      }
    }
  }, [
    cardCheckout,
    clearErrors,
    connect,
    getConnectorStrict,
    isConnecting,
    paymentMethod,
    setWalletError,
  ])

  const isAbstractWallet = useIsAbstractWalletAccount()
  const cardDisabledReason = useMemo(() => {
    if (checkoutFlow?.type !== "checkout") {
      return t("unsupportedFlow")
    }

    if (checkoutFlow.items.length > 1) {
      return t("unsupportedItemCount")
    }

    if (!cardSupported) {
      return t("unsupportedQuote")
    }

    if (
      isAbstractWallet &&
      checkoutFlow.items.some(item => item.chain.identifier !== "abstract")
    ) {
      return t("unsupportedAbstractWallet")
    }

    return undefined
  }, [checkoutFlow, t, cardSupported])

  return (
    <FlexColumn className="relative w-full min-w-0">
      {!hidePaymentMethod && paymentStep === "payment-method" && (
        <PaymentMethodStep
          cardDisabledReason={cardDisabledReason}
          onProceedToPayment={onProceedToPayment}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
        />
      )}

      {paymentStep === "payment" && (
        <CheckoutPaymentStep
          cardDisabled={Boolean(cardDisabledReason)}
          currencySelector={currencySelector}
          hidePaymentMethodOptions={hidePaymentMethod}
          onChangePaymentMethod={
            hidePaymentMethod
              ? undefined
              : () => setPaymentStep("payment-method")
          }
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          timeline={timeline}
        />
      )}
    </FlexColumn>
  )
}
